import {
	Button,
	cn,
	type DeliveryMethod,
	deliveryMethodIcons,
} from "@au-schein-de/au-ui-library";
import type { Cart } from "@dransay/api/models";
import {
	DELIVERY_METHOD_KIND,
	EXPRESS_COURIER_PROVIDER,
	exceedsUberDirectBasketCap,
	SHIPPING_PROVIDER,
} from "@dransay/api/models";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CartFreeDeliveryProgressBar } from "~/features/cart/cart-free-delivery-progress-bar.component";
import {
	canShowFreeShippingThreshold,
	formatDeliveryPriceLabel,
} from "~/hooks/format-delivery-price.logic";
import {
	formatCentsAsAmount,
	useCurrencyPrice,
} from "~/hooks/use-currency-price.hook";
import { useExpressCourierAvailability } from "~/hooks/use-express-courier-availability";
import { useMarket } from "~/hooks/use-market";
import { useUberShippingQuote } from "~/hooks/use-uber-shipping-quote";
import { expressCourierDisabledTimeHours } from "~/tooling/express-courier-operating-hours";
import {
	resolveDeliveryTimeRange,
	resolveProviderLabel,
} from "./delivery-method-details.resolver";
import { ExpressCourierCard } from "./express-courier-card.component";
import { resolveExpressCourierDisabledReason } from "./express-courier-disabled-reason.resolver";

type VendorDeliveryMethod = NonNullable<
	Cart["vendor"]
>["deliveryMethods"][number];

function cheapestEnabledDeliveryMethod(
	methods: VendorDeliveryMethod[] | undefined,
): VendorDeliveryMethod | undefined {
	if (!methods?.length) {
		return undefined;
	}
	return [...methods].sort(
		(left, right) => (left.cost ?? 0) - (right.cost ?? 0),
	)[0];
}

interface DeliveryMethodCardsProps {
	vendor: NonNullable<Cart["vendor"]>;
	deliveryMethodUuid: Cart["deliveryMethodUuid"];
	updateDeliveryMethod: (
		deliveryMethodUuid: NonNullable<Cart["deliveryMethodUuid"]>,
	) => Promise<void>;
	productsPrice: number;
	customerPostalCode?: string;
	/**
	 * Hides the heading above the option list. The wording names the pharmacy,
	 * so it does not belong to a non-pharmacy vendor's package. The radio group
	 * keeps it as its accessible name.
	 */
	hideOptionsLabel?: boolean;
	showFreeDeliveryProgress?: boolean;
	/**
	 * Checkout adds the pharmacy-handled line under courier. Cart leaves it off.
	 */
	showAdditionalInfo?: boolean;
	/**
	 * Checkout asks Uber after the address is saved. Cart leaves this off.
	 * The key is the saved address and phone; the server does not read it.
	 */
	requestUberQuote?: boolean;
	uberQuoteAddressKey?: string;
}

export function DeliveryMethodCards({
	vendor,
	deliveryMethodUuid,
	updateDeliveryMethod,
	productsPrice,
	customerPostalCode,
	hideOptionsLabel = false,
	showFreeDeliveryProgress = false,
	showAdditionalInfo = false,
	requestUberQuote = false,
	uberQuoteAddressKey = "",
}: DeliveryMethodCardsProps) {
	const { t } = useTranslation(["cart", "product", "checkout", "common"]);
	const { getCurrencyPrice } = useCurrencyPrice();
	const market = useMarket();

	const expressCourierMethod = useMemo(
		() =>
			vendor.deliveryMethods?.find(
				(m) =>
					m.kind === DELIVERY_METHOD_KIND.EXPRESS_COURIER &&
					(m.enabled ||
						m.expressUnavailableReason === "time" ||
						m.expressUnavailableReason === "location" ||
						m.expressUnavailableReason === "day"),
			),
		[vendor.deliveryMethods],
	);

	const isExpressCourierSelected =
		!!expressCourierMethod && deliveryMethodUuid === expressCourierMethod.uuid;
	// Uber does not use the Lieferando fulfillment /check. A day without
	// delivery, a closed Berlin window, or a point outside 10 km still leaves
	// the pharmacy's method on the card, greyed, via expressUnavailableReason.
	const isUberDirectExpress =
		expressCourierMethod?.provider === EXPRESS_COURIER_PROVIDER.UBER_DIRECT;
	const uberUnavailableReason =
		isUberDirectExpress &&
		(expressCourierMethod?.expressUnavailableReason === "time" ||
			expressCourierMethod?.expressUnavailableReason === "location" ||
			expressCourierMethod?.expressUnavailableReason === "day")
			? expressCourierMethod.expressUnavailableReason
			: undefined;
	const uberLocalGatesPass =
		isUberDirectExpress &&
		expressCourierMethod?.enabled === true &&
		uberUnavailableReason == null;
	const { outcome: uberQuoteOutcome } = useUberShippingQuote({
		vendorId: vendor.id,
		addressKey: uberQuoteAddressKey,
		enabled: requestUberQuote && uberLocalGatesPass,
	});
	const quoteDisablesUber =
		requestUberQuote &&
		uberLocalGatesPass &&
		uberQuoteOutcome !== null &&
		uberQuoteOutcome !== "available";
	const quoteDisabledReason = quoteDisablesUber
		? uberQuoteOutcome === "location"
			? ("location" as const)
			: ("unavailable" as const)
		: undefined;

	const { status: checkedExpressStatus, isLoading: isCheckedExpressLoading } =
		useExpressCourierAvailability({
			vendorId: vendor.id,
			postalCode: customerPostalCode,
			enabled: !!expressCourierMethod && !isUberDirectExpress,
		});
	const expressCourierStatus = isUberDirectExpress
		? "available"
		: checkedExpressStatus;
	const isExpressCourierChecking = isUberDirectExpress
		? false
		: isCheckedExpressLoading;

	const isUberBasketOverCap =
		isUberDirectExpress && exceedsUberDirectBasketCap(productsPrice);
	const expressCourierDisabledReason = isUberBasketOverCap
		? "basket"
		: (uberUnavailableReason ??
			quoteDisabledReason ??
			resolveExpressCourierDisabledReason({
				status: expressCourierStatus,
				isSelected: isExpressCourierSelected,
				isChecking: isExpressCourierChecking,
			}));

	const showExpressCourier =
		!!expressCourierMethod &&
		(expressCourierStatus === "available" ||
			!!expressCourierDisabledReason ||
			isExpressCourierSelected);

	const dateFormatter = useMemo(
		() =>
			new Intl.DateTimeFormat(market.languageTag, {
				hour: "2-digit",
				minute: "2-digit",
			}),
		[market.languageTag],
	);

	const formatOrderBefore = useCallback(
		(orderBefore: string) => {
			const [hours, minutes, seconds = 0] = orderBefore.split(":").map(Number);
			if ([hours, minutes, seconds].some((value) => !Number.isFinite(value))) {
				return orderBefore;
			}
			const date = new Date();
			date.setHours(hours, minutes, seconds, 0);
			return dateFormatter.format(date);
		},
		[dateFormatter],
	);

	const getShippingSuffix = useCallback(
		(deliveryMethod: VendorDeliveryMethod) => {
			if (deliveryMethod.kind !== "shipping") {
				return null;
			}
			switch (deliveryMethod.provider) {
				case SHIPPING_PROVIDER.DHL_EXPRESS:
					return t("deliveryMethods.shipping.express", { ns: "checkout" });
				case SHIPPING_PROVIDER.DHL_STANDARD:
					return t("deliveryMethods.shipping.standard", { ns: "checkout" });
				default:
					return null;
			}
		},
		[t],
	);

	const getSecondaryDescription = useCallback(
		(deliveryMethod: VendorDeliveryMethod) => {
			switch (deliveryMethod.kind) {
				case "shipping":
					return resolveProviderLabel(deliveryMethod.provider);
				case "pickup": {
					if (!deliveryMethod.orderBefore || !deliveryMethod.type) {
						return null;
					}
					const hour = formatOrderBefore(deliveryMethod.orderBefore);
					return deliveryMethod.type === "same-day"
						? t("deliveryMethods.pickup.sameDay", { ns: "checkout", hour })
						: t("deliveryMethods.pickup.nextDay", { ns: "checkout", hour });
				}
				case "courier": {
					if (!deliveryMethod.orderBefore || !deliveryMethod.type) {
						return null;
					}
					const hour = formatOrderBefore(deliveryMethod.orderBefore);
					return deliveryMethod.type === "same-day"
						? t("deliveryMethods.courier.sameDay", { ns: "checkout", hour })
						: t("deliveryMethods.courier.nextDay", { ns: "checkout", hour });
				}
				default:
					return null;
			}
		},
		[t, formatOrderBefore],
	);

	const getDeliveryTime = useCallback(
		(deliveryMethod: VendorDeliveryMethod) => {
			const range = resolveDeliveryTimeRange(deliveryMethod);
			return range
				? t("deliveryMethods.shipping.deliveryTime", {
						ns: "checkout",
						...range,
					})
				: null;
		},
		[t],
	);

	const [isPending, setIsPending] = useState(false);

	const handleSelect = useCallback(
		async (uuid: string) => {
			if (isPending || uuid === deliveryMethodUuid) {
				return;
			}
			setIsPending(true);
			try {
				await updateDeliveryMethod(uuid);
			} finally {
				setIsPending(false);
			}
		},
		[updateDeliveryMethod, isPending, deliveryMethodUuid],
	);

	const handleExpressCourierSelect = useCallback(() => {
		if (expressCourierMethod) {
			handleSelect(expressCourierMethod.uuid);
		}
	}, [handleSelect, expressCourierMethod]);

	const getDeliveryPriceLabel = useCallback(
		(
			deliveryMethodKind: VendorDeliveryMethod["kind"],
			deliveryPrice: number | null | undefined,
			deliveryMethodFreeFrom: number | null | undefined,
		) => {
			const formatted = formatDeliveryPriceLabel({
				deliveryPrice,
				deliveryMethodKind,
				totalPrice: productsPrice,
				deliveryMethodFreeFrom,
				formatCurrency: getCurrencyPrice,
				pendingPriceLabel: t("pricePending", { ns: "common" }),
			});
			return {
				priceLabel: formatted.before ?? formatted.after,
				isFreeDelivery: formatted.before !== null,
			};
		},
		[getCurrencyPrice, productsPrice, t],
	);

	const enabledMethods = useMemo(
		() =>
			vendor.deliveryMethods?.filter(
				(method) =>
					method.enabled &&
					method.kind !== DELIVERY_METHOD_KIND.EXPRESS_COURIER,
			),
		[vendor.deliveryMethods],
	);

	const selectedDeliveryMethod = useMemo(
		() =>
			vendor.deliveryMethods?.find(
				(method) => method.uuid === deliveryMethodUuid,
			),
		[vendor.deliveryMethods, deliveryMethodUuid],
	);

	// A disabled card (FR-062) must not leave the cart pointed at a delivery
	// method that cannot actually be fulfilled — otherwise checkout proceeds
	// with a method that will fail later. Fall back to the first other
	// enabled method once, per mismatch episode (guarded by the ref so a
	// pending updateDeliveryMethod call isn't re-dispatched on every render
	// while the prop hasn't caught up yet). The guard is only latched once
	// the dispatch actually starts — not when handleSelect would no-op
	// because a selection is already pending — and is released again if the
	// update fails, so a real mismatch is always retried.
	const hasDispatchedFallbackRef = useRef(false);
	useEffect(() => {
		const isStuckOnDisabledExpress =
			isExpressCourierSelected && !!expressCourierDisabledReason;
		if (!isStuckOnDisabledExpress) {
			hasDispatchedFallbackRef.current = false;
			return;
		}
		if (hasDispatchedFallbackRef.current || isPending) {
			return;
		}
		const fallbackMethod = quoteDisabledReason
			? cheapestEnabledDeliveryMethod(enabledMethods)
			: enabledMethods?.[0];
		if (fallbackMethod) {
			hasDispatchedFallbackRef.current = true;
			handleSelect(fallbackMethod.uuid).catch(() => {
				hasDispatchedFallbackRef.current = false;
			});
		}
	}, [
		isExpressCourierSelected,
		expressCourierDisabledReason,
		quoteDisabledReason,
		enabledMethods,
		handleSelect,
		isPending,
	]);

	return (
		<div className="flex flex-col">
			{!hideOptionsLabel && (
				<span className="mb-4 text-body text-gray-500">
					{t("availableDelivery", { ns: "cart" })}
				</span>
			)}
			{showFreeDeliveryProgress && (
				<CartFreeDeliveryProgressBar
					deliveryCost={selectedDeliveryMethod?.cost}
					freeFrom={selectedDeliveryMethod?.freeFrom}
					merchandiseTotalCents={productsPrice}
				/>
			)}
			<div
				className="flex flex-col gap-2.5"
				role="radiogroup"
				aria-label={t("availableDelivery", { ns: "cart" })}
			>
				{showExpressCourier && (
					<ExpressCourierCard
						isSelected={
							isExpressCourierSelected && !expressCourierDisabledReason
						}
						isPending={isPending}
						onSelect={handleExpressCourierSelect}
						provider={expressCourierMethod.provider}
						operatingHours={expressCourierDisabledTimeHours(
							expressCourierMethod.provider,
							expressCourierMethod.days,
						)}
						disabledReason={expressCourierDisabledReason}
						priceLabel={
							getDeliveryPriceLabel(
								expressCourierMethod.kind,
								expressCourierMethod.cost,
								expressCourierMethod.freeFrom,
							).priceLabel
						}
					/>
				)}
				{enabledMethods?.map((deliveryMethod) => {
					const isSelected =
						!isExpressCourierSelected &&
						deliveryMethodUuid === deliveryMethod.uuid;
					const { priceLabel, isFreeDelivery } = getDeliveryPriceLabel(
						deliveryMethod.kind,
						deliveryMethod.cost,
						deliveryMethod.freeFrom,
					);
					const hasFreeThreshold =
						canShowFreeShippingThreshold(
							deliveryMethod.cost,
							deliveryMethod.freeFrom,
						) && !isFreeDelivery;
					const secondaryDescription = getSecondaryDescription(deliveryMethod);
					const deliveryTime = getDeliveryTime(deliveryMethod);
					const additionalInfo =
						showAdditionalInfo && deliveryMethod.kind === "courier"
							? t("deliveryMethods.courier.pharmacyHandled", {
									ns: "checkout",
								})
							: null;

					return (
						<DeliveryMethodCard
							key={deliveryMethod.uuid}
							deliveryMethod={deliveryMethod}
							isSelected={isSelected}
							isFreeDelivery={isFreeDelivery}
							hasFreeThreshold={hasFreeThreshold}
							secondaryDescription={secondaryDescription}
							additionalInfo={additionalInfo}
							deliveryTime={deliveryTime}
							shippingSuffix={getShippingSuffix(deliveryMethod)}
							freeThresholdLabel={
								hasFreeThreshold
									? t("delivery.freeFrom", {
											ns: "common",
											amount: formatCentsAsAmount(
												deliveryMethod.freeFrom ?? 0,
												market.languageTag,
											),
										})
									: null
							}
							freeLabel={t("deliveryMethods.free", { ns: "cart" })}
							methodLabel={t(`deliveryMethods.${deliveryMethod.kind}`, {
								ns: "product",
							})}
							priceLabel={priceLabel}
							isPending={isPending}
							onSelect={() => handleSelect(deliveryMethod.uuid)}
						/>
					);
				})}
			</div>
		</div>
	);
}

interface DeliveryMethodCardProps {
	deliveryMethod: VendorDeliveryMethod;
	isSelected: boolean;
	isFreeDelivery: boolean;
	hasFreeThreshold: boolean;
	secondaryDescription: string | null;
	additionalInfo: string | null;
	deliveryTime: string | null;
	shippingSuffix: string | null;
	freeThresholdLabel: string | null;
	freeLabel: string;
	methodLabel: string;
	priceLabel: string;
	isPending: boolean;
	onSelect: () => void;
}

function DeliveryMethodCard({
	deliveryMethod,
	isSelected,
	isFreeDelivery,
	hasFreeThreshold,
	secondaryDescription,
	additionalInfo,
	deliveryTime,
	shippingSuffix,
	freeThresholdLabel,
	freeLabel,
	methodLabel,
	priceLabel,
	isPending,
	onSelect,
}: DeliveryMethodCardProps) {
	return (
		<Button
			variant="ghost"
			className={cn(
				"w-full flex flex-col items-start gap-2 p-4 text-left whitespace-normal transition-colors cursor-pointer rounded-xl h-auto [&>span]:w-full [&>span]:flex-col [&>span]:items-start [&>span]:gap-2",
				isSelected
					? "border-2 border-ansay-main bg-white"
					: "border-2 border-transparent bg-gray-100 hover:bg-gray-200",
				isPending && !isSelected && "pointer-events-none",
			)}
			disabled={isPending && !isSelected}
			onClick={onSelect}
			// biome-ignore lint/a11y/useSemanticElements: native <input type="radio"> causes layout shifts inside Dialog, and isn't following the Button type "ghost" structure we have in similar cases
			role="radio"
			aria-checked={isSelected}
			aria-label={`${methodLabel}${shippingSuffix ? `: ${shippingSuffix}` : ""}, ${priceLabel}${isFreeDelivery ? `, ${freeLabel}` : ""}`}
		>
			<div className="flex items-center gap-2 w-full min-w-0">
				<span
					className={cn(
						"shrink-0 [&_svg]:text-current",
						isSelected ? "text-ansay-700" : "text-gray-900",
					)}
				>
					{deliveryMethodIcons[deliveryMethod.kind as DeliveryMethod]}
				</span>
				<span
					className={cn(
						"text-body-bold capitalize",
						isSelected ? "text-ansay-700" : "text-gray-900",
					)}
				>
					{methodLabel}
					{shippingSuffix && `: ${shippingSuffix}`}
				</span>
				<div className="flex-grow" />
				{hasFreeThreshold && freeThresholdLabel && (
					<span className="inline-flex items-center shrink-0 rounded bg-ansay-background text-caption text-ansay-700 h-7 px-2 whitespace-nowrap">
						{freeThresholdLabel}
					</span>
				)}
				{isFreeDelivery && (
					<span className="shrink-0 text-body text-gray-500 line-through whitespace-nowrap">
						{priceLabel}
					</span>
				)}
				<span
					className={cn(
						"shrink-0 text-body-bold whitespace-nowrap",
						isSelected ? "text-ansay-700" : "text-gray-900",
					)}
				>
					{isFreeDelivery ? freeLabel : priceLabel}
				</span>
			</div>
			{(secondaryDescription || deliveryTime) && (
				<div className="flex justify-between items-start gap-2 text-gray-500 text-body-sm w-full min-w-0">
					{secondaryDescription && (
						<span className="min-w-0 break-words">{secondaryDescription}</span>
					)}
					{deliveryTime && (
						<span className="shrink-0 whitespace-nowrap ml-auto">
							{deliveryTime}
						</span>
					)}
				</div>
			)}
			{additionalInfo && (
				<p
					className="text-body-sm text-gray-500"
					data-testid="delivery-method-additional-info"
				>
					{additionalInfo}
				</p>
			)}
		</Button>
	);
}
